import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinkClass = ({ isActive }) => `text-lg font-semibold transition duration-300 ${
    isActive 
      ? 'text-blue-300' 
      : 'text-white hover:text-blue-200'
  } drop-shadow-lg`;

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-black/80 backdrop-blur-md' : 'bg-gradient-to-r from-blue-900 via-black to-blue-900'
    }`}>
      <nav className="container mx-auto px-4 sm:px-6 py-2 sm:py-4 relative z-10">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center group">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="relative"
            >
              <img src="/images/CompanyLogo.png" alt="共伸機工株式会社ロゴ" className="h-12 sm:h-16 w-auto mr-2 sm:mr-4 drop-shadow-lg" />
              <div className="absolute inset-0 bg-blue-400 filter blur-md opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
            </motion.div>
            <div className="flex flex-col">
              <motion.h1 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="text-xl sm:text-3xl font-bold text-white drop-shadow-lg"
                style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
              >
                共伸機工株式会社
              </motion.h1>
              <motion.span
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-xs sm:text-lg font-semibold tracking-wider text-blue-200 drop-shadow-lg"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                KYOSHIN KIKO CO., LTD.
              </motion.span>
            </div>
          </Link>
          <div className="hidden md:flex space-x-4 lg:space-x-6">
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <NavLink to="/" className={navLinkClass} end>ホーム</NavLink>
            </motion.div>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <NavLink to="/company" className={navLinkClass}>会社案内</NavLink>
            </motion.div>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <NavLink to="/news" className={navLinkClass}>NEWS</NavLink>
            </motion.div>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <NavLink to="/services" className={navLinkClass}>修理サービス</NavLink>
            </motion.div>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <NavLink to="/products" className={navLinkClass}>製品特集</NavLink>
            </motion.div>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <NavLink to="/contact" className={navLinkClass}>お問い合わせ</NavLink>
            </motion.div>
          </div>
          <motion.button 
            className="md:hidden text-2xl focus:outline-none text-white drop-shadow-lg"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "メニューを閉じる" : "メニューを開く"}
            aria-expanded={isMenuOpen}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            {isMenuOpen ? '×' : '☰'}
          </motion.button>
        </div>
      </nav>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed top-0 right-0 h-full w-64 bg-gradient-to-b from-blue-900 to-black shadow-lg z-50 md:hidden"
          >
            <div className="flex flex-col h-full">
              <motion.button 
                className="self-end p-4 text-2xl focus:outline-none text-white"
                onClick={toggleMenu}
                aria-label="メニューを閉じる"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                ×
              </motion.button>
              <nav className="flex flex-col space-y-4 p-4">
                <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                  <NavLink to="/" className={navLinkClass} onClick={toggleMenu} end>ホーム</NavLink>
                </motion.div>
                <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                  <NavLink to="/company" className={navLinkClass} onClick={toggleMenu}>会社案内</NavLink>
                </motion.div>
                <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                  <NavLink to="/news" className={navLinkClass} onClick={toggleMenu}>NEWS</NavLink>
                </motion.div>
                <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
                  <NavLink to="/services" className={navLinkClass} onClick={toggleMenu}>修理サービス</NavLink>
                </motion.div>
                <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
                  <NavLink to="/products" className={navLinkClass} onClick={toggleMenu}>製品特集</NavLink>
                </motion.div>
                <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
                  <NavLink to="/contact" className={navLinkClass} onClick={toggleMenu}>お問い合わせ</NavLink>
                </motion.div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;